/**
 * Amounts travel as integer minor units, so formatting is integer arithmetic: dividing by
 * 100 into a float first is what puts 1250.0000000000002 on a screen.
 */
export function formatAmount(amountMinor: number, currency: string): string {
  const units = Math.floor(amountMinor / 100);
  const cents = amountMinor % 100;
  const groupedUnits = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${currency} ${groupedUnits},${String(cents).padStart(2, '0')}`;
}

export const PRICE_CURRENCY = 'USD';

/// The same rule the backend applies: digits, an optional period and at most two decimals.
const PRICE_PATTERN = /^\d{1,9}(\.\d{1,2})?$/;

/// The largest price the backend stores (a PostgreSQL INTEGER of cents): USD 21.474.836,47.
const MAX_PRICE_MINOR = 2_147_483_647;

/**
 * `subject` names the field as the sentence starts: "El precio pagado", "El valor ajustado".
 *
 * Zero is refused because it is not what anybody paid or sells for, it is a price nobody
 * entered. The upper bound is the column's, not a business rule, so its number is not shown:
 * no phone comes near it, and naming it would read as a limit somebody chose.
 */
export function priceError(value: string, subject: string): string | null {
  const normalized = value.trim();

  if (!PRICE_PATTERN.test(normalized) || !/[1-9]/.test(normalized)) {
    return `${subject} tiene que ser un monto en USD mayor que cero, con punto decimal y hasta dos decimales.`;
  }

  // Digits only, so the cents come out exact: no float ever holds the amount.
  const [units, decimals = ''] = normalized.split('.');

  if (Number(units) * 100 + Number(decimals.padEnd(2, '0')) > MAX_PRICE_MINOR) {
    return `${subject} es demasiado alto.`;
  }

  return null;
}

/// What a price input holds, in minor units; null while it is not a valid price.
export function priceInputToMinor(value: string): number | null {
  if (priceError(value, 'El precio')) return null;
  const [units, decimals = ''] = value.trim().split('.');
  return Number(units) * 100 + Number(decimals.padEnd(2, '0'));
}

/// A stored price back into the text an input edits, keeping its cents: "270" or "270.50".
export function toPriceInput(amountMinor: number | null): string {
  if (amountMinor === null) {
    return '';
  }

  const units = Math.floor(amountMinor / 100);
  const cents = amountMinor % 100;

  return cents ? `${units}.${String(cents).padStart(2, '0')}` : String(units);
}

export function formatPrice(amountMinor: number | null): string {
  return amountMinor === null ? 'Sin cargar' : formatAmount(amountMinor, PRICE_CURRENCY);
}

/// "USD 20,00 menos que el publicado (USD 500,00)": how far the charge is from the sheet's price
/// (P-E4). Null when there is nothing to compare.
export function priceDifferenceLabel(
  finalPriceMinor: number | null,
  publishedPriceMinor: number | null,
  format: (amount: number) => string,
): string | null {
  if (finalPriceMinor === null || publishedPriceMinor === null) return null;
  const difference = finalPriceMinor - publishedPriceMinor;
  if (difference === 0) return 'Igual al precio publicado.';
  return `${format(Math.abs(difference))} ${difference < 0 ? 'menos' : 'más'} que el publicado (${format(publishedPriceMinor)}).`;
}
